// tableWidth.jsx
import React from 'react'

/**
 * TableWidthContext — der Kanal „die Tabelle meldet, der Kopf übernimmt" (UI2 Teil 10, G0b.4).
 *
 * @description
 *   Zwei Bausteine brauchen dieselbe Zahl und dürfen sie nicht selbst messen:
 *   `TableViewport` (BS14/BW6) **kennt** die Tabellenbreite — `min(Behälter,
 *   Spaltenbudget)`, Konzept `inhaltsbreite.md` §4.1:568 — und der Kopf
 *   (`ViewHeader`, BW3) **trägt sie als seinen Deckel** (§4.2:594, NW4.3:649).
 *   Das Konzept legt fest, WER meldet und WER stufenweise übernimmt
 *   (Plan `…g0-bausteine.md` §3.2, 🔴-Absatz „vier Zuteilungen", :631-639), aber
 *   **nicht den Weg dazwischen**; dieser Baustein ist der Weg.
 *
 *   🔴 **Warum ein eigener Kontext und kein Prop.** Der Kopf steht in der Regel
 *   **über** der Tabelle, nicht in ihr: er ist ihr Geschwister im Seitenbaum,
 *   nicht ihr Kind. Ein Prop müsste durch jede Seite gereicht werden, die beide
 *   rendert. Ein Kontext trägt die Zahl von der Tabelle zum Kopf, ohne dass eine
 *   Seite sie kennt — dieselbe Bauform wie `PageWidthContext` (G0b.2).
 *
 *   🔴 **Verworfen: ein Rückruf-Prop am Kopf (`onTableWidth`) mit einer
 *   `useState`-Zelle in jeder Seite.** Er zwingt **jede** der künftigen
 *   Ansichtskarten (G1–G15), denselben Zustand von Hand zu führen und ihn an
 *   zwei Stellen zu verdrahten — dieselbe Zahl in zwei Quellen, und die Seite
 *   müsste den Kopf und die Tabelle gemeinsam besitzen. Der Kontext stellt den
 *   Zustand **einmal** (`TableWidthProvider`) und lässt beide Bausteine
 *   unabhängig davon stehen.
 *
 *   🔴 **Rein additiv.** Der Vorgabewert ist `{ tableWidth: null, searchMax: null,
 *   melde: null }` — **ohne** Provider ist `melde` `null`, die Tabelle meldet
 *   nichts, und der Kopf bleibt bei seinem Musterdeckel (dem heutigen Wert). Kein
 *   bestehender Verbraucher ändert sein Verhalten.
 *
 *   🔴 **Und die Meldung wird ZURÜCKGENOMMEN, wenn die Tabelle verschwindet**
 *   (R16-W3): `TableViewport` setzt beim Abbau ihres Effekts `null`. Ohne das
 *   trüge der Kopf weiter den Deckel einer Tabelle, die es nicht mehr gibt
 *   (Ladezustand, leerer Zustand, Tabwechsel). `null` heißt damit zweierlei und
 *   beides richtig: „noch nichts gemeldet" **und** „nichts mehr gemeldet" — der
 *   Kopf fällt in beiden Fällen auf seinen Musterdeckel zurück.
 *
 *   🔴 **Die Rücknahme ist EIGENTÜMER-GEPRÜFT — sie nimmt nur die EIGENE Meldung
 *   zurück** (R15-W1, R16-W1). Der Kanal trägt höchstens **eine** Meldung; ohne
 *   Kennung nähme eine abhängende Tabelle die Meldung einer anderen mit. Jeder
 *   Melder gibt deshalb eine **Kennung** mit (`TableViewport` nimmt `useId`),
 *   der Provider merkt sich den **Eigentümer** der stehenden Meldung, und eine
 *   Rücknahme (`null`) wirkt nur, wenn der Kanal **noch diesem** Melder gehört.
 *   **Die zuletzt meldende Tabelle gewinnt** — meldet B nach A, gehört der Kanal
 *   B; hängt **A** ab, bleibt Bs Meldung stehen; hängt **B** ab, fällt der Kanal
 *   auf `null`, obwohl A noch steht. 🔴 **Je Provider meldet deshalb EINE Tabelle**
 *   (R16-R3): mehrere meldende sind ein Baufehler (`/rma/sla-overview` rendert drei).
 *
 *   ⚠️ **Der Rückfall auf den Musterdeckel beim Abbau ist gewollt, kein
 *   Nebeneffekt** (R16-W2): der Kanal trägt EINE Meldung, ihr Eigentümer ist der
 *   letzte Schreiber (R15-R3-W1). Er geschieht in **einem** Schritt, nicht
 *   stufenweise — die Stufenleiter (`ViewHeader.jsx` `naechsteStufe`, NW4.6:652)
 *   gilt nur beim **Übernehmen** einer gemeldeten Breite, nicht beim Rückfall.
 *
 *   🔴 **Gemeldet wird nie eine `0`** (R16-Blocker 1, R15-W2): der Kopf nähme sie
 *   als Deckel an und liefe auf `maxWidth: 0` herunter. `TableViewport` meldet
 *   deshalb nur bei `tabellenBreite > 0` — ein leeres Spaltenbudget und eine
 *   angesagte Behälterbreite `0` ergeben beide keine Meldung.
 *
 *   🔴 **Was hier NICHT hineingehört.** Nicht die Musterbreite — das ist
 *   `PageWidthContext` (G0b.2). Nicht die gemessene Behälterbreite — die kommt
 *   aus `useContainerWidth` (BW4/G0b.3) und ist ein **Argument** des Aufrufers.
 *   Dieser Kontext trägt genau zwei gemeldete Werte: die **Tabellenbreite** und
 *   den **Suchdeckel** (`SEARCH_MAX` auf einer `full`-Seite, NW4.2:648).
 *
 * @useWhen
 *   - Eine Tabelle meldet ihre Breite an den Kopf ihrer Seite (`TableViewport` → `ViewHeader`)
 *   - Eine Seite hat keine Tabelle, der Kopf soll aber dieselbe Bündigkeit tragen — dann bleibt der Kanal leer und der Kopf bei seinem Muster
 *
 * @dontUseFor
 *   - Das Breiten**muster** der Seite — das ist `PageWidthContext`
 *   - Die gemessene Behälterbreite — die liefert `useContainerWidth` als Rückgabewert
 *   - Eine Zahl, die nur EINE Komponente braucht — dafür genügt ein Prop
 *
 * @related
 *   - `PageWidthContext` (`PageContainer.jsx`) — der zweite Kontext desselben Bausteins; er trägt das Muster, dieser die gemessene Breite
 *   - `TableViewport` (BS14/BW6) — der Melder
 *   - `ViewHeader` (BW3) — der Übernehmer (stufenweise, NW4.6:652)
 *   - `ListToolbar` (BS2) — liest den Suchdeckel
 *
 * @reuseHints
 *   - Der Fuß einer Tabelle (`TableFooterSimple`) soll später auf **derselben** Breite sitzen (§4.2:599) — er liest dieselbe Zahl
 *   - Ein Skelett einer Listenseite kann die zuletzt gemeldete Breite tragen, statt einen eigenen Deckel zu führen
 *
 * @props
 *   value.tableWidth — `number | null`: die gemeldete Tabellenbreite; `null` = „noch nichts gemeldet"
 *   value.searchMax — `number | null`: der Deckel des Suchfelds in Z1 (`SEARCH_MAX` auf `full`, NW4.2)
 *   value.melde — `((stand, kennung) => void) | null`: der Setter; `null` ohne Provider (dann meldet niemand). `stand = { tableWidth, searchMax }` meldet, `stand = null` nimmt zurück — und die Rücknahme wirkt nur für den Melder, dem der Kanal gerade gehört
 */
export const TableWidthContext = React.createContext({
  tableWidth: null,
  searchMax: null,
  melde: null,
});

/**
 * TableWidthProvider — hält den gemeldeten Stand für einen Seitenbaum.
 *
 * 🔴 **Den Provider stellt `PageContainer`** — **einer je Seite, und die Seite
 * muss EINEN Baum haben.** Damit muss kein Aufrufer ihn von Hand setzen. Eine
 * Seite **ohne** `PageContainer` (die Musterroute `/design/muster` ist die eine)
 * kann ihn selbst legen — er rendert kein DOM und ändert kein Layout.
 *
 * 🔴 **Geschwister-Container sind der Gegenfall, und er ist nicht theoretisch.**
 * Zwei Seiten rendern **zwei** `PageContainer` als Geschwister — einer trägt den
 * `ViewHeader`, einer den Inhalt:
 *   - `frontend/src/PODetail.jsx` — Kopf `:3598`, Inhalt `:3617`
 *   - `frontend/src/ProductDetail.jsx` — Kopf `:2031`, Inhalt `:2483` (geladener Zweig)
 * Der Kopf liest den **ersten** Provider, eine Tabelle im Inhalt meldet in den
 * **zweiten** — die Meldung erreicht den Kopf **nie**, ohne Fehler und ohne
 * Warnung. 🔴 **Die Zusage lautet deshalb nicht „ein `PageContainer` je Seite
 * genügt"**: eine Seite mit Geschwister-Containern muss den Provider **einmal um
 * beide** legen (`<TableWidthProvider>` um Kopf **und** Inhalt), statt sich auf
 * die zwei `PageContainer` zu verlassen.
 *
 * 🔴 **Gemessen, nicht aufgezählt** (2026-10-12, `grep -n "<PageContainer"`
 * über `frontend/src`): sechs Dateien öffnen mehr als einen — `PODetail` (5),
 * `ProductDetail` (4), `CompetitorList` (2), `ScraperList` (3), `RMADetail` (3),
 * `PODetailSkeleton` (2). Davon sind **genau die zwei oben** Geschwister im
 * selben Baum; die übrigen setzen den Kopf **in denselben** Container wie den
 * Inhalt (`CompetitorList.jsx:367`/`:439`, `ScraperList.jsx:1337`/`:2431`)
 * oder trennen ihn über **einander ausschließende** Zweige (`RMADetail` Lade-/
 * Fehler-/Ladezweig, `ProductDetail.jsx:610`/`:1942`). Wer eine neue Seite baut,
 * prüft ihren Fall selbst — die Liste hier ist eine Messung mit Datum, keine
 * Zusage.
 * 🔴 **G9b (2026-10-21):** `CompetitorList` öffnet nur noch **zwei** Container
 * (`:367`/`:439`) — der Fehlerzweig ist in den geladenen Zweig gefallen (die
 * Leiste bleibt sichtbar und wird gesperrt, N8.5). Die Zahl oben ist mitgezogen.
 *
 * @useWhen
 *   - `PageContainer` — für jede Seite mit Rahmen, die EINEN Baum hat
 *   - Eine Seite ohne `PageContainer`, deren Kopf und Tabelle sich abstimmen sollen
 *   - Eine Seite mit **Geschwister**-`PageContainer`n (Kopf und Inhalt getrennt) —
 *     dann den Provider **einmal um beide** legen
 *
 * @dontUseFor
 *   - Zweimal verschachteln: der innere Provider überschriebe den äußeren (je Seite genau einer)
 *   - **Geschwister**: zwei `PageContainer` nebeneinander sind zwei Kanäle. Der Kopf liest
 *     den ersten, die Tabelle meldet in den zweiten — die Meldung kommt nie an
 *     (`PODetail.jsx:3598`/`:3617`). Dann den Provider um beide legen
 */
export function TableWidthProvider({ children }) {
  const [stand, setStand] = React.useState({ tableWidth: null, searchMax: null, eigentuemer: null });
  // 🔴 Der Setter ist stabil und schreibt nur bei echter Änderung — sonst liefe
  //    eine Meldung im Effekt der Tabelle bei jedem Rendern einen neuen Zyklus.
  // 🔴 Und er ist EIGENTÜMER-GEPRÜFT (R15-W1/R16-W1): `stand = null` nimmt nur
  //    zurück, wenn der Kanal noch die Meldung DIESES Melders trägt. Meldet eine
  //    zweite Tabelle danach, gehört der Kanal ihr — die erste darf ihn beim
  //    Abbau nicht leeren. Ohne Kennung (Alt-Aufrufer) bleibt das alte Verhalten:
  //    beide Seiten tragen `undefined` und treffen einander.
  //    🔴 **Vorbehalt (R16-R3, G0b.5): mehrere meldende Tabellen unter EINEM
  //    Provider sind ein BAUFEHLER; die Beschreibung hier ist das Verhalten des
  //    Netzes, keine zugesagte Lage.** Je Provider meldet EINE Tabelle.
  const melde = React.useCallback((naechster, kennung) => {
    setStand((alt) => {
      if (naechster == null) {
        return alt.eigentuemer === kennung
          ? { tableWidth: null, searchMax: null, eigentuemer: null }
          : alt;
      }
      return alt.eigentuemer === kennung
        && alt.tableWidth === naechster.tableWidth
        && alt.searchMax === naechster.searchMax
        ? alt
        : { tableWidth: naechster.tableWidth, searchMax: naechster.searchMax, eigentuemer: kennung };
    });
  }, []);
  const wert = React.useMemo(
    () => ({ tableWidth: stand.tableWidth, searchMax: stand.searchMax, melde }),
    [stand.tableWidth, stand.searchMax, melde],
  );
  return <TableWidthContext.Provider value={wert}>{children}</TableWidthContext.Provider>;
}

/**
 * useTableWidth — liest den gemeldeten Stand (`tableWidth`, `searchMax`, `melde`).
 *
 * @useWhen
 *   - Der Kopf holt die Tabellenbreite, die Werkzeugleiste den Suchdeckel
 *   - Eine Tabelle holt `melde`, um ihren Stand zu melden
 *
 * @dontUseFor
 *   - Ein Baustein, der nur sein eigenes Maß braucht — dann `CONTENT_WIDTH`/`DETAIL_RAIL_WIDTH` direkt
 *
 * @returns {{ tableWidth: number|null, searchMax: number|null, melde: Function|null }}
 */
export function useTableWidth() {
  return React.useContext(TableWidthContext);
}
